import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { LoaderIcon, TriangleAlert } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import {
	ADDRESS_ENTRY_SCHEMA,
	AddressEntryFields,
	type AddressEntryValues,
	parseCoordinate,
	useAddressEntryLabels,
} from "@/components/shared/address-entry-fields";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	type CorrectionRequest,
	correctionRequest,
	onlySeenConflicts,
	type RelocationCheck,
	sendJson,
} from "@/lib/therapist-relocation";
import { cn } from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";
import { ConflictList } from "./conflict-list";

/** The timeline entry being corrected, with the address Go stores for it. */
export interface CorrectableEntry {
	therapistAddressId: number;
	effectiveFrom: string | null;
	effectiveUntil: string | null;
	address: {
		locationId: number;
		latitude: number;
		longitude: number;
		address: string;
		postalCode: string | null;
		notes: string | null;
	};
}

const valuesOf = ({ address }: CorrectableEntry): AddressEntryValues => ({
	address: address.address,
	postalCode: address.postalCode ?? "",
	coordinate: `${address.latitude}, ${address.longitude}`,
	locationId: address.locationId,
	notes: address.notes ?? "",
});

// A 409's conflicts stay valid while the address is unchanged; the reason is not checked.
const keyOf = ({ address }: CorrectionRequest) => JSON.stringify(address);

export function CorrectAddressDialog({
	entry,
	onClose,
	onSaved,
	relocationsPath,
	locations,
}: {
	/** null keeps the dialog closed */
	entry: CorrectableEntry | null;
	onClose: () => void;
	/** Reloads what the correction changed. */
	onSaved: () => void;
	/** The correction patches under it. */
	relocationsPath: string;
	locations: Pick<Location, "id" | "city" | "state" | "country">[];
}) {
	const { t } = useTranslation("therapists", {
		keyPrefix: "relocation.correct",
	});
	const { t: tRelocation } = useTranslation("therapists", {
		keyPrefix: "relocation",
	});
	const { locale } = useDateContext();
	const day = (value: string) => format(parseISO(value), "PP", { locale });
	const addressLabels = useAddressEntryLabels();
	const fieldId = useId();

	const [reason, setReason] = useState("");
	const [check, setCheck] = useState<{
		key: string;
		data: RelocationCheck;
	} | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [attempted, setAttempted] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const outcomeRef = useRef<HTMLDivElement>(null);

	const addressForm = useForm<AddressEntryValues>({
		resolver: zodResolver(ADDRESS_ENTRY_SCHEMA),
		mode: "onChange",
	});
	const address = useWatch({ control: addressForm.control });

	const parsed = correctionRequest({
		reason,
		address: {
			locationId: Number(address.locationId) || 0,
			address: address.address ?? "",
			postalCode: address.postalCode,
			notes: address.notes,
			coordinate: parseCoordinate(address.coordinate),
		},
	});
	const issueAt = (...path: string[]) =>
		parsed.error?.issues.some(
			(issue) => issue.path.join(".") === path.join("."),
		);
	const coordinateError = attempted && issueAt("address", "coordinate");
	const reasonError = attempted && issueAt("reason");

	const fresh = parsed.success && check?.key === keyOf(parsed.data);
	const conflicts = fresh ? (check?.data.conflicts ?? []) : [];

	// Every opening starts from the entry's stored address.
	useEffect(() => {
		if (!entry) return;
		setReason("");
		setCheck(null);
		setMessage(null);
		setAttempted(false);
		setConfirming(false);
		addressForm.reset(valuesOf(entry));
	}, [entry, addressForm]);

	useEffect(() => {
		if (message)
			outcomeRef.current?.scrollIntoView({
				block: "start",
				behavior: "smooth",
			});
	}, [message]);

	// The first save goes without acknowledge_conflicts and doubles as the conflict check. Go's
	// yes covers every conflict at save time, so it is only resent for visits already shown.
	const save = async () => {
		if (!entry) return;
		setAttempted(true);
		if (!(await addressForm.trigger()) || !parsed.success) return;
		const request = parsed.data;
		const url = `${relocationsPath}/${entry.therapistAddressId}`;
		setMessage(null);
		setSaving(true);
		try {
			let result = await sendJson(
				url,
				{ ...request, acknowledge_conflicts: false },
				"PATCH",
			);
			if (
				result.status === 409 &&
				result.data &&
				conflicts.length > 0 &&
				onlySeenConflicts(result.data.conflicts, conflicts)
			) {
				result = await sendJson(
					url,
					{ ...request, acknowledge_conflicts: true },
					"PATCH",
				);
			}

			if (result.ok) {
				onClose();
				onSaved();
			} else if (result.status === 409 && result.data) {
				setCheck({ key: keyOf(request), data: result.data });
				setMessage(
					t(conflicts.length > 0 ? "errors.new_conflicts" : "errors.conflicts"),
				);
			} else {
				setMessage(result.error || t("errors.save_failed"));
			}
		} catch {
			setMessage(t("errors.save_failed"));
		} finally {
			setSaving(false);
			setConfirming(false);
		}
	};

	const requestSave = () => {
		if (conflicts.length > 0) setConfirming(true);
		else save();
	};

	const dates = entry
		? [
				entry.effectiveFrom &&
					tRelocation("from", { date: day(entry.effectiveFrom) }),
				entry.effectiveUntil &&
					tRelocation("until", { date: day(entry.effectiveUntil) }),
			]
				.filter(Boolean)
				.join(" · ")
		: "";

	return (
		<>
			<Dialog
				open={!!entry}
				onOpenChange={(next) => {
					if (!next && !saving) onClose();
				}}
			>
				<DialogContent className="flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-2xl">
					<DialogHeader className="p-4 pb-3 border-b sm:p-6 sm:pb-4">
						<DialogTitle>{t("title")}</DialogTitle>
						<DialogDescription>{t("description")}</DialogDescription>
					</DialogHeader>

					{/* A fieldset cannot be the scroll box: as a flex item Chromium neither clips nor scrolls it. */}
					<div className="min-h-0 p-4 overflow-y-auto sm:p-6">
						<fieldset disabled={saving} className="grid min-w-0 gap-5">
							<div className="grid gap-1.5">
								<Label>{t("dates")}</Label>
								<p className="text-sm text-muted-foreground">{dates || "-"}</p>
							</div>

							<Form {...addressForm}>
								<div className="grid min-w-0 gap-1.5">
									<AddressEntryFields
										form={addressForm}
										locations={locations}
										labels={addressLabels}
										className="grid min-w-0 gap-4"
										notesClassName="min-h-16"
										mapHeight="200px"
									/>
									{coordinateError && (
										<p role="alert" className="text-xs text-destructive">
											{t("errors.coordinate")}
										</p>
									)}
								</div>
							</Form>

							<div className="grid gap-1.5">
								<Label htmlFor={`${fieldId}-reason`}>{t("reason.label")}</Label>
								<Textarea
									id={`${fieldId}-reason`}
									value={reason}
									onChange={(event) => setReason(event.target.value)}
									placeholder={t("reason.placeholder")}
									aria-invalid={reasonError}
									className="shadow-inner bg-input"
								/>
								{reasonError && (
									<p role="alert" className="text-xs text-destructive">
										{t("errors.reason")}
									</p>
								)}
							</div>

							{/* The outcome lands below a long form; bring it into view whenever it changes. */}
							<div
								ref={outcomeRef}
								className="grid gap-5 empty:hidden scroll-mt-4"
							>
								{message && (
									<Alert variant="destructive">
										<TriangleAlert className="size-4" />
										<AlertDescription>{message}</AlertDescription>
									</Alert>
								)}

								{check && (
									<section
										aria-live="polite"
										className={cn("grid gap-2", !fresh && "opacity-50")}
									>
										<h3 className="text-sm font-semibold">
											{t("conflicts_title", {
												count: check.data.conflicts.length,
											})}
										</h3>
										<ConflictList conflicts={check.data.conflicts} />
									</section>
								)}
								{check && !fresh && (
									<p className="-mt-3 text-xs text-muted-foreground">
										{t("stale")}
									</p>
								)}
							</div>
						</fieldset>
					</div>

					<DialogFooter className="gap-2 p-4 border-t sm:p-6 sm:pt-4">
						<Button variant="outline" disabled={saving} onClick={onClose}>
							{t("cancel")}
						</Button>
						<Button disabled={saving} onClick={requestSave}>
							{saving && <LoaderIcon className="animate-spin" />}
							{t("save")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={confirming}
				onOpenChange={(next) => {
					if (!saving) setConfirming(next);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("confirm.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("confirm.description", { count: conflicts.length })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={saving}>
							{t("confirm.back")}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={saving}
							onClick={(event) => {
								// keep the dialog open until the request finishes
								event.preventDefault();
								save();
							}}
						>
							{saving && <LoaderIcon className="animate-spin" />}
							{t("confirm.save")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
